import React, {
    useCallback,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import Button from './Button';

/**
 * Overlay popdown menu with a trigger that supports optional icon + text.
 * Icon-only triggers use the same layout/classes as Button variant="icon".
 */
function ComboDropdown({
    triggerText,
    triggerIcon,
    triggerStyle,
    triggerClassName = '',
    'aria-label': ariaLabel,
    items = [],
    align = 'start',
    open: openControlled,
    defaultOpen = false,
    onOpenChange,
    menuStyle,
    id: idProp,
}) {
    const reactId = useId();
    const baseId = idProp ?? `combo-${reactId.replace(/:/g, '')}`;
    const triggerId = `${baseId}-trigger`;
    const menuId = `${baseId}-menu`;

    const [openUncontrolled, setOpenUncontrolled] = useState(defaultOpen);
    const isControlled = openControlled !== undefined;
    const open = isControlled ? openControlled : openUncontrolled;

    const setOpen = useCallback(
        (next) => {
            if (!isControlled) {
                setOpenUncontrolled(next);
            }
            onOpenChange?.(next);
        },
        [isControlled, onOpenChange],
    );

    const containerRef = useRef(null);
    const triggerRef = useRef(null);
    const itemRefs = useRef([]);

    const showLabel = Boolean(triggerText?.trim());
    if (!showLabel && !ariaLabel) {
        throw new Error(
            'ComboDropdown: provide triggerText or aria-label for accessibility.',
        );
    }

    const close = useCallback(() => setOpen(false), [setOpen]);

    useEffect(() => {
        if (!open) return undefined;

        const onDocPointerDown = (e) => {
            const el = containerRef.current;
            if (el && !el.contains(e.target)) {
                close();
            }
        };

        document.addEventListener('mousedown', onDocPointerDown);
        document.addEventListener('touchstart', onDocPointerDown, {
            passive: true,
        });
        return () => {
            document.removeEventListener('mousedown', onDocPointerDown);
            document.removeEventListener('touchstart', onDocPointerDown);
        };
    }, [open, close]);

    useEffect(() => {
        if (!open) return undefined;

        const onKey = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                close();
                triggerRef.current?.focus();
                return;
            }

            const enabledIndices = items
                .map((it, i) => (!it.disabled ? i : -1))
                .filter((i) => i >= 0);
            if (enabledIndices.length === 0) return;

            const currentIdx = itemRefs.current.findIndex(
                (node) => node === document.activeElement,
            );
            const pos =
                currentIdx >= 0 ? enabledIndices.indexOf(currentIdx) : -1;

            if (e.key === 'ArrowDown') {
                e.preventDefault();
                const next =
                    pos < enabledIndices.length - 1
                        ? enabledIndices[pos + 1]
                        : enabledIndices[0];
                itemRefs.current[next]?.focus();
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                const next =
                    pos > 0
                        ? enabledIndices[pos - 1]
                        : enabledIndices[enabledIndices.length - 1];
                itemRefs.current[next]?.focus();
            } else if (e.key === 'Home') {
                e.preventDefault();
                itemRefs.current[enabledIndices[0]]?.focus();
            } else if (e.key === 'End') {
                e.preventDefault();
                itemRefs.current[
                    enabledIndices[enabledIndices.length - 1]
                ]?.focus();
            }
        };

        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, close, items]);

    useEffect(() => {
        itemRefs.current = itemRefs.current.slice(0, items.length);
    }, [items]);

    const toggle = () => setOpen(!open);

    const onTriggerKeyDown = (e) => {
        if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
            if (!open) {
                e.preventDefault();
                setOpen(true);
                requestAnimationFrame(() => {
                    const first = items.findIndex((it) => !it.disabled);
                    if (first >= 0) itemRefs.current[first]?.focus();
                });
            }
        }
    };

    const panelAlign =
        align === 'end'
            ? { left: 'auto', right: 0 }
            : { left: 0, right: 'auto' };

    const triggerVariant = showLabel ? 'text' : 'icon';

    return (
        <div
            ref={containerRef}
            className="combo-dropdown"
            style={{ position: 'relative', display: 'inline-block' }}
        >
            <Button
                ref={triggerRef}
                id={triggerId}
                variant={triggerVariant}
                className={`combo-dropdown__trigger${open ? ' combo-dropdown__trigger--open' : ''} ${triggerClassName}`.trim()}
                style={triggerStyle}
                aria-expanded={open}
                aria-haspopup="menu"
                aria-controls={menuId}
                aria-label={showLabel ? undefined : ariaLabel}
                onClick={toggle}
                onKeyDown={onTriggerKeyDown}
            >
                {triggerIcon ? (
                    <span
                        className="combo-dropdown__trigger-icon"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            lineHeight: 0,
                        }}
                    >
                        {triggerIcon}
                    </span>
                ) : null}
                {showLabel ? (
                    <span
                        className="text-body-sm"
                        style={{ color: 'inherit' }}
                    >
                        {triggerText}
                    </span>
                ) : null}
            </Button>

            {open ? (
                <div
                    id={menuId}
                    role="menu"
                    aria-labelledby={triggerId}
                    className="combo-dropdown__panel"
                    style={{
                        position: 'absolute',
                        top: '100%',
                        width: '132px',
                        minWidth: '132px',
                        zIndex: 2000,
                        ...panelAlign,
                        ...menuStyle,
                    }}
                >
                    {items.map((item, index) => {
                        const {
                            key: itemKey,
                            label,
                            icon,
                            onClick,
                            disabled,
                            itemStyle,
                            itemClassName = '',
                        } = item;
                        return (
                            <button
                                key={itemKey}
                                ref={(el) => {
                                    itemRefs.current[index] = el;
                                }}
                                type="button"
                                role="menuitem"
                                tabIndex={-1}
                                disabled={disabled}
                                className={`combo-dropdown__item app-button ${itemClassName}`.trim()}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    border: 'none',
                                    cursor: disabled ? 'not-allowed' : 'pointer',
                                    textAlign: 'left',
                                    gap: '12px',
                                    padding: `4px 10px`,
                                    borderRadius: '6px',
                                    opacity: disabled ? 0.45 : 1,
                                    ...itemStyle,
                                }}
                                onClick={(e) => {
                                    if (disabled) return;
                                    onClick?.(e);
                                    close();
                                    triggerRef.current?.focus();
                                }}
                            >
                                {icon ? (
                                    <span
                                        style={{
                                            display: 'inline-flex',
                                            flexShrink: 0,
                                            lineHeight: 0,
                                        }}
                                    >
                                        {icon}
                                    </span>
                                ) : null}
                                <span
                                    className="text-body-sm"
                                    style={{ color: 'inherit' }}
                                >
                                    {label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            ) : null}
        </div>
    );
}

export default ComboDropdown;
